import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { PostCard } from '../../components/PostCard/PostCard'
import { getCurrentUserId } from '../../utilities/auth'
import { AppLayout } from '../../Layout/AppLayout'
import { countries } from '../../utilities/countries'
import { userCountry, userLanguage } from '../../utilities/random'
import { updateUserProfile } from '../SignupPage/sagas/saga'
import { fetchFeed } from './saga/saga'
import SEO from '../../components/SEO/SEO'
import { getCanonicalUrl } from '../../utilities/seo'

// Universal feed. Single CSS-grid lane that auto-fills columns based on width
// (1 on phones, 2 on small tablets, 3 on laptops, 4+ on wide displays —
// `auto-fill` picks the column count, no hand-picked breakpoints). New posts
// stream in via cursor pagination + IntersectionObserver sentinel.
//
// Country tagging on posts still lives in the schema (per-post `country`),
// but the feed is global — we don't filter by `userCountry` anymore. The
// country flag on each card shows where the listing originates.

const PAGE_SIZE = 24

export default function HomePage() {
  const intl = useIntl()
  const [isProfileUpdated, setIsProfileUpdated] = useState(false)
  const user = useSelector((state) => state.user.user)

  const [items, setItems] = useState([])
  // Owner-aware action on each PostCard (chat for non-owner, edit pencil
  // for own listings that surface in the global feed). Read once on
  // mount; updates if the user signs in/out via the redux user slice.
  const me = user?.id || getCurrentUserId()
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState(false)

  const sentinelRef = useRef(null)
  // Track cursor + loading + done in refs so the IntersectionObserver
  // callback always sees the latest values without re-binding on every
  // state change.
  const stateRef = useRef({ cursor: null, loading: false, done: false })

  const loadMore = useCallback(async () => {
    if (stateRef.current.loading || stateRef.current.done) return
    stateRef.current.loading = true
    setLoading(true)
    try {
      const res = await fetchFeed({ cursor: stateRef.current.cursor, limit: PAGE_SIZE })
      setItems((prev) => [...prev, ...res.items])
      stateRef.current.cursor = res.nextCursor
      setCursor(res.nextCursor)
      if (!res.nextCursor) {
        stateRef.current.done = true
        setDone(true)
      }
      setError(false)
    } catch (e) {
      // API down / network error. Park the loop (`done = true`) so the
      // IntersectionObserver doesn't re-fire into an infinite error storm,
      // and surface a retry. Swallowing the rejection here is what keeps a
      // backend hiccup from tripping the CRA error overlay / crashing.
      stateRef.current.done = true
      setDone(true)
      setError(true)
    } finally {
      stateRef.current.loading = false
      setLoading(false)
    }
  }, [])

  const retry = useCallback(() => {
    stateRef.current.done = false
    setDone(false)
    setError(false)
    loadMore()
  }, [loadMore])

  // Initial load.
  useEffect(() => {
    loadMore()
  }, [loadMore])

  // Backfill the signed-in user's country once (kept from the old HomePage —
  // makes sure the profile has a country recorded even if onboarding didn't
  // set one).
  useEffect(() => {
    if (isProfileUpdated) return
    if (user?.id) {
      updateUserProfile({ userId: user.id, userData: { country: userCountry }, notification: false, noUpdate: true })
      setIsProfileUpdated(true)
    }
  }, [user, isProfileUpdated])

  // Infinite scroll. `rootMargin: 600px` triggers a preload well before the
  // user actually hits the bottom so the feed feels seamless.
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore()
      },
      { rootMargin: '600px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [loadMore])

  const countryName =
    countries.find((item) => item.code.toLowerCase() === userCountry?.toLowerCase())?.label ||
    userCountry
  const homeUrl = getCanonicalUrl('/')
  const homeTitle = intl.formatMessage({
    id: 'home.seo.title',
    defaultMessage: "Sell what you don’t wear",
  })
  const homeDescription = intl.formatMessage({
    id: 'home.seo.description',
    defaultMessage:
      "Sell what you don’t wear, find what you want to. Anyone can post, anyone can shop — clothes, shoes, accessories, basics to designer. Worldwide.",
  })
  const orgDescription = intl.formatMessage({
    id: 'home.seo.orgDescription',
    defaultMessage:
      'Global marketplace where anyone can sell or shop — clothes, shoes, and accessories, basics to designer.',
  })

  return (
    <AppLayout showFab>
      <SEO
        title={homeTitle}
        description={homeDescription}
        url={homeUrl}
        country={userCountry}
        language={userLanguage}
        keywords={[
          'online marketplace',
          'buy clothes',
          'sell clothes',
          countryName,
          'pre-loved fashion',
          'designer fashion',
          'accessories',
          'shoes',
          'global marketplace',
        ]}
        webPage={{ name: `dresez - ${countryName}`, description: homeDescription, url: homeUrl }}
        organization={{
          name: 'dresez',
          url: 'https://dresez.com',
          description: orgDescription,
          // Surfaces the MCP server to search + AI crawlers without any
          // visible UI — gives them a link to discover + crawl mcp.dresez.com.
          sameAs: ['https://mcp.dresez.com'],
        }}
      />
      <div className="w-full flex items-center flex-col">
        <div className="max-w-[1400px] w-full px-2 py-4">
          <div className="mb-4">
            <h1 className="text-[22px] md:text-[26px] font-bold text-black dark:text-white">
              <FormattedMessage
                id="home.hero.title"
                defaultMessage="Sell what you don’t wear. Find what you want to."
              />
            </h1>
            <p className="text-[14px] text-gray-600 dark:text-gray-400 mt-1">
              <FormattedMessage
                id="home.hero.sub"
                defaultMessage="Anyone can post. Anyone can shop. From basics to designer — worldwide."
              />
            </p>
          </div>
          {error && items.length === 0 ? (
            <div className="flex flex-col justify-center items-center w-full h-full mt-[50px] gap-3">
              <p className="text-center text-[20px]">
                <FormattedMessage
                  id="home.error.couldntLoad"
                  defaultMessage="Couldn’t load posts right now."
                />
              </p>
              <button
                type="button"
                onClick={retry}
                className="text-[#3a6df0] underline cursor-pointer text-[18px]"
              >
                <FormattedMessage id="common.retry" defaultMessage="Retry" />
              </button>
            </div>
          ) : (
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
            >
              {items.map((item) => (
                <PostCard key={item.id} post={item} currentUserId={me} />
              ))}
            </div>
          )}

          {/* Sentinel for IntersectionObserver. Shows the loading hint
              while a page is in flight; intentionally blank once we're at
              the end so the feed doesn't tail off with a label. */}
          <div ref={sentinelRef} className="h-12 flex items-center justify-center mt-4 text-gray-400">
            {loading ? (
              <FormattedMessage id="common.loading" defaultMessage="Loading…" />
            ) : error && items.length > 0 ? (
              <button type="button" onClick={retry} className="text-[#3a6df0] underline cursor-pointer">
                <FormattedMessage
                  id="home.error.couldntLoadMore"
                  defaultMessage="Couldn’t load more — Retry"
                />
              </button>
            ) : (
              ''
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
